import {useTranslation} from 'react-i18next';
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  useFieldArray,
  UseFormGetValues,
} from 'react-hook-form';
import {SVG} from '../../../../../../constants/assetLists';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import {FormDataType, Question, createDefaultQuestion} from '../../types';
import QuestionItem from './QuestionItem';
import QuestionAddButton from './QuestionAddButton';

type Props = {
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
  errors: any;
  getValues: UseFormGetValues<FormDataType>;
};

function QuestionsTab({control, watch, setValue, errors, getValues}: Props) {
  const {t} = useTranslation();

  const {fields, append, remove} = useFieldArray({
    control,
    name: 'questions',
  });

  const title = watch('title');

  // Sync single question mandatory -> allQuestionsRequired
  const handleQuestionMandatoryChange = (index: number, value: boolean) => {
    // Let Controller update the field first

    const questions = getValues('questions') || [];

    const updatedQuestions = questions.map((q, i) =>
      i === index ? {...q, mandatory: value} : q,
    );

    const allMandatory = updatedQuestions.every(q => q.mandatory);

    setValue('settings.allQuestionsRequired', allMandatory, {
      shouldDirty: true,
    });
  };

  const handleAddQuestion = () => {
    const isMandatory = watch('settings.allQuestionsRequired');
    const newQuestion = createDefaultQuestion(
      'short-answer',
      `q-${Date.now()}`,
      undefined,
      isMandatory,
    );
    append(newQuestion);
  };

  const handleCloneQuestion = (index: number) => {
    const question = fields[index] as Question;
    const clonedQuestion = {
      ...question,
      id: `q-${Date.now()}`,
    };
    append(clonedQuestion);
  };

  const handleRemoveQuestion = (index: number) => {
    remove(index);
  };

  return (
    <div className="flex flex-col h-full py-4">
      {/* Form Title */}
      <div className="w-full flex-shrink-0  px-4">
        <div className="bg-grayBg px-1 py-2 rounded-tl-[4px] rounded-tr-[4px] border-b-[3px] border-b-linkBlue flex items-center gap-1">
          {!title && (
            <span className="m-0 text-[32px] font-medium text-raspberryRed">
              *
            </span>
          )}
          <input
            className="w-full m-0 text-[28px] text-black bg-transparent outline-none placeholder:text-black"
            placeholder={t('forms.dialog.formTitlePlaceholder')}
            autoComplete="off"
            value={title}
            onChange={e =>
              setValue('title', e.target.value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </div>
        {errors?.title?.message && (
          <p className="m-0 text-[12px] text-raspberryRed mt-1">
            {errors?.title?.message}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto mt-4 px-4">
        {/* Description */}
        <AppTextInput
          placeholder={t('forms.dialog.descriptionPlaceholder')}
          multiline
          rows={3}
          maxLength={150}
          value={watch('description')}
          onChange={e =>
            setValue('description', e.target.value, {
              shouldDirty: true,
            })
          }
          startIcon={
            <img
              src={SVG.descriptionDarkIcon}
              alt="icon"
              className="w-[16px] h-[14px] object-contain"
            />
          }
          className="border-none"
        />

        {/* Questions List — scrollable */}
        <div className="mt-2 flex flex-col gap-4">
          {fields.map((field, index) => (
            <QuestionItem
              key={field.id}
              index={index}
              control={control}
              watch={watch}
              setValue={setValue}
              onClone={() => handleCloneQuestion(index)}
              onRemove={() => handleRemoveQuestion(index)}
              onMandatoryChange={(
                checked: React.ChangeEvent<HTMLInputElement>,
              ) => {
                const value = checked.target.checked; // extract boolean
                handleQuestionMandatoryChange(index, value);
              }}
              errors={errors?.questions?.[index]}
            />
          ))}
        </div>

        {/* Add Question Button */}
        <div className="mt-4">
          <QuestionAddButton onAdd={handleAddQuestion} />
        </div>
      </div>
    </div>
  );
}

export default QuestionsTab;
